import { db } from './firebaseConfig';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  onSnapshot,
  getDocs,
  doc,
  getDoc,
  Timestamp,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';

// Activity types
export const ACTIVITY_TYPES = {
  REGISTRATION: 'registration',
  ACHIEVEMENT: 'achievement',
  EXERCISE_COMPLETED: 'exercise_completed'
};

export const activityUtils = {
  // Get recent activity notifications, respecting privacy
  getRecentActivities: async function(maxResults = 20) {
    try {
      const activitiesRef = collection(db, "activities");
      const q = query(
        activitiesRef,
        where("public", "==", true),
        orderBy("timestamp", "desc"),
        limit(maxResults)
      );
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        
        // Calculate time ago in minutes
        const timestamp = data.timestamp.toDate();
        const now = new Date();
        const diffMs = now - timestamp;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        
        return {
          id: doc.id,
          user: data.username || 'Jemand', // Use "Jemand" as fallback, don't expose full username
          city: data.city || 'Deutschland',
          activity: data.activity,
          timeAgo: diffMinutes,
          type: data.type
        };
      });
    } catch (error) {
      console.error("Error fetching recent activities:", error);
      return [];
    }
  },
  
  // Register a new activity
  logActivity: async function(data) {
    try {
      const { userId, username, city, type, activity, public: isPublic = true, metadata = {} } = data;
      
      // If any required field is missing, don't log
      if (!type || !activity) {
        console.error("Missing required fields for activity logging");
        return false;
      }
      
      // Create activity document with privacy in mind
      const activityData = {
        type,
        activity,
        timestamp: serverTimestamp(),
        public: isPublic,
        metadata
      };
      
      // Only add user/location info if provided and public
      if (userId && isPublic) {
        activityData.userId = userId;
      }
      
      if (username && isPublic) {
        // Only store first name/initial for privacy
        const firstNameOnly = username.split(' ')[0];
        activityData.username = firstNameOnly.length > 3 
          ? firstNameOnly
          : 'Jemand'; // Use "Jemand" for very short names for privacy
      }
      
      // Check if the user has enabled activity sharing
      const { db } = await import('./firebaseConfig');
      if (userId) {
        try {
          const userDoc = await getDoc(doc(db, "users", userId));
          const userData = userDoc.data();
          
          // Only add location if user has explicitly enabled activity sharing
          if (userData && userData.showActivities === true && city) {
            activityData.city = city;
          } else {
            // User has not consented to activity sharing
            activityData.public = false;
            return false;
          }
        } catch (error) {
          console.error("Error checking user activity permissions:", error);
          activityData.public = false;
          return false;
        }
      } else if (city && isPublic) {
        // For non-user activities or backwards compatibility
        activityData.city = city;
      } else {
        // Default to no sharing if no location
        activityData.public = false;
        return false;
      }
      
      // Add to Firestore
      const docRef = await addDoc(collection(db, "activities"), activityData);
      return !!docRef.id;
    } catch (error) {
      console.error("Error logging activity:", error);
      return false;
    }
  },
  
  // Setup realtime activity listener
  listenForActivities: function(callback, maxResults = 20) {
    try {
      const activitiesRef = collection(db, "activities");
      const q = query(
        activitiesRef,
        where("public", "==", true),
        orderBy("timestamp", "desc"),
        limit(maxResults)
      );
      
      // Set up real-time listener
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const activities = snapshot.docs.map(doc => {
          const data = doc.data();
          
          // Calculate time ago in minutes
          let timeAgo = 1; // Default to 1 minute
          if (data.timestamp) {
            const timestamp = data.timestamp.toDate();
            const now = new Date();
            const diffMs = now - timestamp;
            timeAgo = Math.floor(diffMs / (1000 * 60));
          }
          
          return {
            id: doc.id,
            user: data.username || 'Jemand',
            city: data.city || 'Deutschland',
            activity: data.activity,
            timeAgo: timeAgo,
            type: data.type
          };
        });
        
        callback(activities);
      });
      
      // Return the unsubscribe function
      return unsubscribe;
    } catch (error) {
      console.error("Error setting up activity listener:", error);
      callback([]);
      return () => {}; // Return empty function as fallback
    }
  }
};